import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import assignmentService from '@/shared/services/assignmentService';
import {
    usePreviewLock,
    PREVIEW_LOCK_NOTE_ID,
} from '@/shared/hooks/usePreviewLock';
import PreviewLockNote from '@/components/common/PreviewLockNote';

export const formatSentDate = (value) =>
    new Date(value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/Denver',
    });

/**
 * The onboarding email to one accepted student: the program's template filled
 * in, editable, with the address it goes to. Sending is the one primary
 * action; onSent receives { sentAt, sentBy } so the row can say it was sent.
 */
function OnboardingEmailDialog({ assignment, onHide, onSent }) {
    const previewLocked = usePreviewLock();
    const [draft, setDraft] = useState(null);
    const [message, setMessage] = useState('');
    const [loadError, setLoadError] = useState('');
    const [sendError, setSendError] = useState('');
    const [sending, setSending] = useState(false);
    const box = useRef(null);
    const id = assignment?._id;

    useEffect(() => {
        if (!id) return undefined;
        let ignore = false;
        setDraft(null);
        setLoadError('');
        setSendError('');
        assignmentService
            .getOnboardingDraft(id)
            .then((answer) => {
                if (ignore) return;
                setDraft(answer);
                setMessage(answer.message);
            })
            .catch((err) => {
                if (ignore) return;
                setLoadError(
                    err.response?.data?.message ||
                        "The email couldn't be loaded. Close this and try again."
                );
            });
        return () => {
            ignore = true;
        };
    }, [id]);

    const send = async () => {
        setSending(true);
        setSendError('');
        try {
            const sent = await assignmentService.sendOnboardingEmail(
                id,
                message
            );
            onSent(sent);
        } catch (err) {
            setSendError(
                err.response?.data?.message ||
                    "The email wasn't sent. Check your connection and try again."
            );
            setSending(false);
        }
    };

    const student = assignment?.peerSupporter;
    const empty = message.trim().length === 0;

    return (
        <Modal
            show={Boolean(assignment)}
            onHide={sending ? undefined : onHide}
            onEntered={() => box.current?.focus()}
            aria-labelledby="onboarding-email-title"
            centered
        >
            <Modal.Header closeButton={!sending}>
                <Modal.Title as="h2" id="onboarding-email-title" className="h5">
                    Onboarding email
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loadError && <Alert variant="danger">{loadError}</Alert>}
                {!loadError && !draft && (
                    <div className="text-center py-4" role="status">
                        <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                        />
                        Loading the email...
                    </div>
                )}
                {draft && (
                    <>
                        <p className="mb-2">
                            <span className="fw-semibold">To: </span>
                            <span className="text-break">
                                {draft.to.name || student?.name}
                                {draft.to.email && ` <${draft.to.email}>`}
                            </span>
                        </p>
                        {draft.sentAt && (
                            <Alert variant="info" className="py-2">
                                {`Sent ${formatSentDate(draft.sentAt)}${
                                    draft.sentBy?.name
                                        ? ` by ${draft.sentBy.name}`
                                        : ''
                                }. Sending again emails the student again.`}
                            </Alert>
                        )}
                        <div className="border-start border-3 ps-3 mb-3 text-body-secondary">
                            {draft.fixedParagraphs.map((paragraph, index) => (
                                <p key={index} className="mb-1 text-break">
                                    {paragraph}
                                </p>
                            ))}
                        </div>
                        <Form.Group controlId="onboarding-email-message">
                            <Form.Label>Your message</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={9}
                                ref={box}
                                maxLength={draft.maxLength}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                disabled={sending}
                            />
                            <Form.Text muted>
                                The line above always comes first. Change the
                                rest as you like before you send.
                            </Form.Text>
                        </Form.Group>
                        <PreviewLockNote locked={previewLocked} />
                        {sendError && (
                            <Alert variant="danger" className="mt-3 mb-0">
                                {sendError}
                            </Alert>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="outline-primary"
                    onClick={onHide}
                    disabled={sending}
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={send}
                    disabled={!draft || empty || sending || previewLocked}
                    aria-describedby={
                        previewLocked ? PREVIEW_LOCK_NOTE_ID : undefined
                    }
                >
                    {sending ? 'Sending...' : 'Send email'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default OnboardingEmailDialog;
